import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { Box, Group, SimpleGrid } from '@mantine/core'
import { AppButton, AppIconButton, AppModal, AppText } from '../../app/common/ui'
import { FiTrash2 } from 'react-icons/fi'
import agent from '../../app/api/agent'
import type { ContentPhotoDto } from '../../app/models/contentPhoto'
import { ContentPhotoOwnerType } from '../../app/models/contentPhoto'
import { useStore } from '../../app/stores/store'

type EntityType = 'Part' | 'FirearmModel';

function ownerTypeForEntity (entityType: EntityType): number {
  return entityType === 'Part' ? ContentPhotoOwnerType.Part : ContentPhotoOwnerType.FirearmModel
}

interface Props {
  entityType: EntityType;
  entityId: string;
}

export default observer(function CommunityPhotosGallery ({ entityType, entityId }: Props) {
  const { userStore } = useStore()
  const [photos, setPhotos] = useState<ContentPhotoDto[]>([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const loadPhotos = useCallback(() => {
    if (!entityId) {
      setPhotos([])
      setLoading(false)
      return
    }
    setLoading(true)
    const ot = ownerTypeForEntity(entityType)
    agent.ContentPhotos.list(ot, entityId)
      .then(setPhotos)
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false))
  }, [entityId, entityType])

  useEffect(() => {
    loadPhotos()
  }, [loadPhotos])

  const handlePickFile = () => fileRef.current?.click()

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setAdding(true)
    try {
      await agent.ContentPhotos.upload(ownerTypeForEntity(entityType), entityId, file)
      toast.success('Photo added.')
      loadPhotos()
    } catch {
      /* interceptor */
    } finally {
      setAdding(false)
    }
  }

  const handleDelete = async (photo: ContentPhotoDto) => {
    try {
      await agent.ContentPhotos.delete(photo.id)
      toast.success('Photo removed.')
      loadPhotos()
      if (lightboxUrl === photo.url) setLightboxUrl(null)
    } catch {
      /* interceptor */
    }
  }

  return (
    <Box
      mt="xl"
      pt="lg"
      sx={{ borderTop: '1px solid var(--gs-border)' }}
    >
      <Group position="apart" mb="md">
        <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 16 }}>
          Community photos
        </AppText>
        {userStore.isLoggedIn && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => void handleFileChange(e)}
            />
            <AppButton size="xs" variant="light" loading={adding} onClick={handlePickFile}>
              Add photo
            </AppButton>
          </>
        )}
      </Group>

      {loading
        ? (
        <AppText size="sm" color="dimmed">
          Loading photos…
        </AppText>
          )
        : photos.length === 0
          ? (
        <AppText size="sm" color="dimmed" sx={{ fontStyle: 'italic' }}>
          No community photos yet. Add different configurations of this {entityType === 'Part' ? 'part' : 'model'}.
        </AppText>
            )
          : (
        <SimpleGrid cols={3} spacing="sm" breakpoints={[{ maxWidth: 'sm', cols: 2 }]}>
          {photos.map((p) => (
            <Box key={p.id} sx={{ position: 'relative' }}>
              <Box
                component="button"
                type="button"
                onClick={() => setLightboxUrl(p.url)}
                sx={{
                  display: 'block',
                  width: '100%',
                  padding: 0,
                  border: '1px solid var(--gs-border)',
                  borderRadius: 8,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  background: 'var(--gs-surface2)'
                }}
              >
                <Box
                  component="img"
                  src={p.url}
                  alt=""
                  sx={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                />
              </Box>
              {userStore.isAdmin && (
                <AppIconButton
                  size="sm"
                  color="red"
                  variant="filled"
                  sx={{ position: 'absolute', top: 6, right: 6 }}
                  aria-label="Delete photo"
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleDelete(p)
                  }}
                >
                  <FiTrash2 size={14} />
                </AppIconButton>
              )}
            </Box>
          ))}
        </SimpleGrid>
            )}

      <AppModal opened={!!lightboxUrl} onClose={() => setLightboxUrl(null)} size="xl" title="Photo">
        {lightboxUrl && (
          <Box component="img" src={lightboxUrl} alt="" sx={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
        )}
      </AppModal>
    </Box>
  )
})
